import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton } from '../../components/ui/GlassComponents';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { FiSearch, FiUsers, FiEye, FiTrash2, FiUserCheck, FiUserX, FiShield } from 'react-icons/fi';

// Helper function to get proper logo URL
const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  
  // If it's already a full URL, return it
  if (logoPath.startsWith('http')) {
    return logoPath;
  }
  
  // If it's a relative path starting with /uploads/
  if (logoPath.startsWith('/uploads/')) {
    return `${axios.defaults.baseURL}${logoPath}`;
  }
  
  // If it contains /uploads/ but doesn't start with it (extract the path)
  if (logoPath.includes('/uploads/')) {
    const uploadPath = logoPath.substring(logoPath.indexOf('/uploads/'));
    return `${axios.defaults.baseURL}${uploadPath}`;
  }
  
  // Default case: prepend the base URL
  return `${axios.defaults.baseURL}${logoPath}`;
};


const AdminUsers = () => {
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [userActivities, setUserActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, filter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/users', {
        params: {
          page: currentPage,
          filter
        }
      });
      setUsers(response.data.users);
      setTotalUsers(typeof response.data.totalUsers === 'number' ? response.data.totalUsers : 0);
      setTotalPages(typeof response.data.totalPages === 'number' ? response.data.totalPages : 1);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role);
    setIsModalOpen(true);
    
    // Fetch user's recent activities
    setLoadingActivities(true);
    try {
      const response = await axios.get(`/api/admin/logs?userId=${user.id}&limit=30`);
      if (response.data.success) {
        setUserActivities(response.data.activities);
      } else {
        toast.error('Failed to fetch user activities');
        setUserActivities([]);
      }
    } catch (error) {
      console.error('Error fetching user activities:', error);
      toast.error('Error fetching user activities');
      setUserActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleSuspendUser = async (userId) => {
    const ok = await confirm({
      title: 'Suspend this user?',
      description: 'They will lose access until reactivated.',
      confirmLabel: 'Suspend',
      destructive: true,
    });
    if (!ok) return;

    try {
      await axios.put(`/api/admin/users/${userId}/suspend`);
      fetchUsers();
      toast.success('User suspended successfully');
    } catch (error) {
      console.error('Error suspending user:', error);
      toast.error('Failed to suspend user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.put(`/api/admin/users/${userId}/activate`);
      fetchUsers();
      toast.success('User activated successfully');
    } catch (error) {
      console.error('Error activating user:', error);
      toast.error('Failed to activate user');
    }
  };

  const handleDeleteUser = async (userId) => {
    const ok = await confirm({
      title: 'Permanently delete this user?',
      description: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      setIsDeleting(true);
      await axios.delete(`/api/admin/users/${userId}`);
      fetchUsers();
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleRoleChange = async (userId) => {
    if (!selectedRole) {
      return toast.error('Please select a role');
    }
    
    if (selectedUser.role === selectedRole) {
      return; // No change
    }
    
    try {
      setIsUpdatingRole(true);
      const response = await axios.put(`/api/admin/users/${userId}/role`, {
        role: selectedRole
      });
      
      // Update the user in the list
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: selectedRole } : user
      ));
      
      // Update the selected user
      setSelectedUser({ ...selectedUser, role: selectedRole });
      
      toast.success('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error(error.response?.data?.message || 'Failed to update user role');
      // Reset the selected role to the current user role
      setSelectedRole(selectedUser.role);
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const formatRole = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'team_admin':
        return 'Team Admin';
      default:
        return 'Team Member';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <GlassCard className="p-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="text-gray-600 dark:text-gray-400">Loading users...</span>
          </div>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Manage Users
          </h1>
          <p className="text-gray-600 dark:text-gray-400">View and manage all users in your organization</p>
        </div>
        
        {/* Search and Filter */}
        <GlassCard className="mb-6 p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <div className="sm:w-48">
              <select
                value={filter}
                onChange={handleFilterChange}
                className="w-full pl-3 pr-10 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-sm text-gray-900 dark:text-white"
              >
                <option value="all">All Users</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="team_admin">Team Admins</option>
                <option value="member">Team Members</option>
                <option value="super_admin">Super Admins</option>
              </select>
            </div>
          </div>
        </GlassCard>
      </div>
      
        {/* Users List */}
        <GlassCard className="overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
              <FiUsers className="h-5 w-5 mr-2 text-blue-500" />
              Users ({filteredUsers.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200/50 dark:divide-gray-700/50">
              <thead className="bg-gray-50/50 dark:bg-gray-800/50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                User
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                Team
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                Role
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                Created
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full" />
                        ) : (
                          <span className="text-lg font-medium text-gray-500 dark:text-gray-400">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.Team ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{user.Team.name}</div>
                        <div className="flex items-center mt-1">
                          {user.Team.logo ? (
                            <img 
                              src={getLogoUrl(user.Team.logo)} 
                              alt={user.Team.name} 
                              className="h-4 w-4 mr-1 rounded-sm object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="h-4 w-4 mr-1 bg-gray-200 dark:bg-gray-700 rounded-sm flex items-center justify-center"
                            style={{ display: user.Team.logo ? 'none' : 'flex' }}
                          >
                            <span className="text-xs text-gray-500">{user.Team.name.charAt(0)}</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.Team.plan || 'No Plan'}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400">No Team</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.role === 'super_admin' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-purple-400" fill="currentColor" viewBox="0 0 8 8">
                          <circle cx="4" cy="4" r="3" />
                        </svg>
                        Super Admin
                      </span>
                    ) : user.role === 'team_admin' ? (
                      <span className="text-sm text-blue-600 dark:text-blue-400">{formatRole(user.role)}</span>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">{formatRole(user.role)}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                      {user.isActive ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewUser(user)}
                      className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 mr-4"
                    >
                      View
                    </button>
                    {user.isActive ? (
                      <button
                        onClick={() => handleSuspendUser(user.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 mr-4"
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivateUser(user.id)}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-4"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      disabled={isDeleting || user.role === 'super_admin'}
                      title={user.role === 'super_admin' ? "Cannot delete super admin users" : "Delete user"}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </GlassCard>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      {/* User Details Modal */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">User Details</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0 h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  {selectedUser.avatar ? (
                    <img src={selectedUser.avatar} alt={selectedUser.name} className="h-16 w-16 rounded-full" />
                  ) : (
                    <span className="text-2xl font-medium text-gray-500 dark:text-gray-400">
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="ml-4">
                  <h4 className="text-xl font-medium text-gray-900 dark:text-white">{selectedUser.name}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Role</h5>
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                    >
                      <option value="team_member">Team Member</option>
                      <option value="team_admin">Team Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <button
                      onClick={() => handleRoleChange(selectedUser.id)}
                      disabled={isUpdatingRole || selectedUser.role === selectedRole}
                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-500 dark:hover:bg-primary-600"
                    >
                      {isUpdatingRole ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating
                        </span>
                      ) : 'Update Role'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Current role: {formatRole(selectedUser.role)}
                  </p>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Team</h5>
                  {selectedUser.Team ? (
                    <div>
                      <div className="flex items-center">
                        {selectedUser.Team.logo ? (
                          <img 
                            src={getLogoUrl(selectedUser.Team.logo)} 
                            alt={selectedUser.Team.name} 
                            className="h-5 w-5 mr-2 rounded-sm object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : (
                          <div className="h-5 w-5 mr-2 bg-gray-200 dark:bg-gray-700 rounded-sm flex items-center justify-center">
                            <span className="text-xs text-gray-500">{selectedUser.Team.name.charAt(0)}</span>
                          </div>
                        )}
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedUser.Team.name}</p>
                      </div>
                      <div className="mt-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                          <span className="capitalize mr-2">{selectedUser.Team.plan || 'No Plan'}</span>
                          {selectedUser.Team.planUserLimit && (
                            <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                              {selectedUser.Team.userCount || 0}/{selectedUser.Team.planUserLimit} licenses
                            </span>
                          )}
                        </div>
                        {selectedUser.Team.isActive ? (
                          <span className="text-xs text-green-600 dark:text-green-400">Active Team</span>
                        ) : (
                          <span className="text-xs text-red-600 dark:text-red-400">Suspended Team</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No Team</p>
                  )}
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Status</h5>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedUser.isActive ? 'Active' : 'Suspended'}
                  </p>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Created</h5>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {new Date(selectedUser.createdAt).toLocaleDateString()} {new Date(selectedUser.createdAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>
              
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Contact Information</h5>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedUser.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Position</p>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedUser.position || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Department</p>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedUser.department || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedUser.location || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Activity Timeline (last 30)</h5>
                {loadingActivities ? (
                  <div className="flex justify-center py-4">
                    <svg className="animate-spin h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : userActivities.length > 0 ? (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                      {userActivities.map((activity) => (
                        <li key={activity.id} className="px-4 py-3">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-900 dark:text-white">
                                <span className="font-medium">{activity.action}</span> - {activity.entityType}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(activity.createdAt).toLocaleString()}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {activity.description}
                            </div>
                            {activity.ipAddress && (
                              <div className="text-xs text-gray-400 dark:text-gray-500">
                                IP: {activity.ipAddress}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-center">
                      <Link to={`/admin/logs?userId=${selectedUser.id}`} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
                        View full activity with search & filters →
                      </Link>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No recent activity found</p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 text-right">
              <button
                onClick={handleCloseModal}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
